import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import { Avatar, Card, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { DevelopmentTask } from '@/types';

/**
 * 발달기록 — 구간 진입 시 자동 생성되는 기록 태스크 목록 (§6.3, §6.7-1).
 * 기존 "우리 반 기록"의 발달 탭을 독립 메뉴로 분리했다(알림장 중복 정리).
 */
export function DevelopmentTasksPage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['development', 'tasks'],
    queryFn: () => apiData<DevelopmentTask[]>('/development/tasks'),
  });

  return (
    <Screen>
      <AppBar title="발달기록" back />

      {isPending && <Spinner />}
      {isError && (
        <ErrorNote message="기록 할 일을 불러오지 못했어요." onRetry={() => void refetch()} />
      )}

      {!isPending && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState
          title="지금 시작할 기록이 없어요"
          body="아이가 새 발달 구간에 들어서면 여기에 알려 드릴게요."
        />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {data?.map((task) => (
          <Card key={task.id} as="li" className="p-0">
            <Link
              to={`/records/development/${task.childId}`}
              className="flex items-center gap-3 p-4"
            >
              <Avatar name={task.child.name} size={44} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">
                  {task.child.name}
                  <span className="ml-1.5 font-normal text-ink-sub">{task.period.label}</span>
                </p>
                <p className="text-sm text-ink-sub">
                  {formatDate(task.dueDate)}까지 마무리하면 좋아요
                </p>
              </div>
              <ChevronRight size={18} className="shrink-0 text-ink-sub" />
            </Link>
          </Card>
        ))}
      </ul>
    </Screen>
  );
}
